import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CellRendererProps } from '../../../DataTable.types';
import { truncateByCharacters } from '../../../../../../utils/utils';
import Tooltip from '../../../../Tooltip/Tooltip';
import CaretIcon from '../../../../../../assets/Svg/CaretIcon';
import { COLORS } from '../../../../../../utils/constants/colors';
import MultiSelectWithDisabledOptions from '../../../../MultiSelectDropdown/MultiSelectWithDisabledOptions';
import { useOutsideClick } from '../../../../../../utils/constants/hooks';
import styles from './MultiSelectDropdownCellRenderer.module.scss';
import { useFixedDropdownInModal } from '../../../../../../utils/customHooks';
import Button from '../../../../Button/Button';
import FieldTrigger, { FieldCaret, FieldText } from '../../../../FieldTrigger/FieldTrigger';
import { FIELD_MENU_POPUP, FIELD_PLACEHOLDER } from '../../../../FieldTrigger/constants';
import { getHorizontalLabelId } from '../../../DataTable.constants';

const PILL_TOOLTIP_STYLE: React.CSSProperties = { width: 'fit-content', minWidth: 'auto', maxWidth: '240px', left: '-30px' };
// The table pill keeps its original inline list styling; the field variant styles the same list through scss.
const PILL_TOOLTIP_LIST_STYLE: React.CSSProperties = {
    margin: 0,
    paddingLeft: '1em',
    textAlign: 'left',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    color: 'var(--icon-color)',
};
const TOOLTIP_CONTENT_CSS: React.CSSProperties = { textAlign: 'left' };

export interface UpdateMultiSelectDropdownProps {
    row?: any;
    column?: any;
    selectedValues?: string[];
}

interface Option {
    label: string;
    value: string;
}

export const MultiSelectDropdownCellRenderer: React.FC<CellRendererProps> = ({ value, row, column, dynamicProps }) => {
    const { updateMultiSelectDropdown, isDisabled, isConfigTableModalDropDown = false, fieldVariant = false } = dynamicProps ?? {};
    const { dropdownOptions } = column;

    // Track selected values (should be an array from the row data)
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectedValuesRef = useRef<string[]>(selectedValues);
    const isDropdownOpenRef = useRef<boolean>(isDropdownOpen);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const initialValuesRef = useRef<string[]>([]); // Track initial values when dropdown opens

    // Helper function to check if values have changed
    const hasValuesChanged = useCallback(() => {
        return (
            initialValuesRef.current.length !== selectedValuesRef.current.length ||
            !initialValuesRef.current.every((val) => selectedValuesRef.current.includes(val)) ||
            !selectedValuesRef.current.every((val) => initialValuesRef.current.includes(val))
        );
    }, []);

    const dropdownPosition = useFixedDropdownInModal({
        isEnabled: isConfigTableModalDropDown,
        isOpen: isDropdownOpen,
        triggerRef: containerRef,
        dropdownRef,
        onClose: () => {
            setIsDropdownOpen(false);

            if (hasValuesChanged()) {
                updateMultiSelectDropdown({
                    row,
                    column,
                    selectedValues: selectedValuesRef.current,
                });
            }
        },
    });

    // Initialize selected values from the row data
    useEffect(() => {
        if (Array.isArray(value)) {
            setSelectedValues(value);
        } else if (value) {
            // Handle single value as array
            setSelectedValues([value]);
        } else {
            setSelectedValues([]);
        }
    }, [value]);

    useEffect(() => {
        selectedValuesRef.current = selectedValues;
    }, [selectedValues]);

    useEffect(() => {
        const wasOpen = isDropdownOpenRef.current;
        isDropdownOpenRef.current = isDropdownOpen;
        // Store initial values only when dropdown first opens (not on subsequent changes)
        if (isDropdownOpen && !wasOpen) {
            initialValuesRef.current = [...selectedValues];
        }
    }, [isDropdownOpen, selectedValues]);

    // Close dropdown when clicking outside and save changes (only if values changed)
    useOutsideClick(containerRef, () => {
        if (isDropdownOpenRef.current) {
            setIsDropdownOpen(false);

            if (hasValuesChanged() && updateMultiSelectDropdown) {
                updateMultiSelectDropdown({
                    row,
                    column,
                    selectedValues: selectedValuesRef.current,
                });
            }
        }
    });

    const handleToggleSelection = (newSelected: string[]) => {
        setSelectedValues(newSelected);
    };

    // Convert dropdownOptions object to array format for MultiSelectWithDisabledOptions
    const allOptions: Option[] = useMemo(() => {
        if (!dropdownOptions) return [];
        return Object.values(dropdownOptions).map((option: any) => ({
            label: option.label,
            value: option.value,
        }));
    }, [dropdownOptions]);

    // Get selected option objects with their styling
    const selectedOptions = useMemo(() => {
        if (!dropdownOptions || !selectedValues.length) return [];
        return selectedValues.map((val) => dropdownOptions[val]).filter(Boolean);
    }, [selectedValues, dropdownOptions]);

    if (!dropdownOptions) {
        return <div style={{ color: COLORS.GREY }}>{FIELD_PLACEHOLDER.EMPTY}</div>;
    }

    if (!updateMultiSelectDropdown) {
        return null;
    }

    // In field mode the menu follows the trigger width; the table pill keeps its fixed width.
    const menuWidth = fieldVariant ? '100%' : '300px';
    const firstName = selectedOptions[0]?.label ? truncateByCharacters(selectedOptions[0].label, 20) : '';
    const remainingOptions = selectedOptions.slice(1);
    const firstOption = selectedOptions[0];

    // Both variants list every selected option in a tooltip; they differ in the trigger text and tooltip placement.
    const renderSelectionTooltip = (
        tooltipProps: Pick<React.ComponentProps<typeof Tooltip>, 'style' | 'className'>,
        listProps: React.HTMLAttributes<HTMLUListElement>,
        label: React.ReactNode,
        more: React.ReactNode,
    ) => (
        <Tooltip
            direction="bottom"
            {...tooltipProps}
            contentCss={TOOLTIP_CONTENT_CSS}
            usePortal={isConfigTableModalDropDown}
            content={
                <ul {...listProps}>
                    {selectedOptions.map((option, index) => (
                        <li key={index}>{option.label}</li>
                    ))}
                </ul>
            }
        >
            {label}
            {more}
        </Tooltip>
    );

    // Cancels only the menu (reverting unsaved picks); SideDrawer's document-level Escape would otherwise close the drawer.
    const handleFieldKeyDown = (e: React.KeyboardEvent) => {
        if (!fieldVariant || !isDropdownOpen || e.key !== 'Escape') return;
        e.stopPropagation();
        setSelectedValues(initialValuesRef.current);
        setIsDropdownOpen(false);
    };

    const renderFieldTrigger = () => (
        <FieldTrigger
            disabled={isDisabled}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            icon={<FieldCaret open={isDropdownOpen} />}
            aria-haspopup={FIELD_MENU_POPUP}
            aria-expanded={isDropdownOpen}
            labelledBy={getHorizontalLabelId(column?.field)}
        >
            {selectedOptions.length > 0 ? (
                renderSelectionTooltip(
                    { className: styles.FieldTooltip },
                    { className: styles.FieldTooltipList },
                    <FieldText>{firstName}</FieldText>,
                    remainingOptions.length > 0 && <span className={styles.FieldMore}> +{remainingOptions.length} more</span>,
                )
            ) : (
                <FieldText isPlaceholder>{isDisabled ? FIELD_PLACEHOLDER.EMPTY : FIELD_PLACEHOLDER.SELECT}</FieldText>
            )}
        </FieldTrigger>
    );

    return (
        <div ref={containerRef} onKeyDown={handleFieldKeyDown}>
            {fieldVariant ? (
                renderFieldTrigger()
            ) : (
                <Button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isDisabled) return;
                        setIsDropdownOpen((prev) => !prev);
                    }}
                    style={{
                        appearance: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem',
                        whiteSpace: 'nowrap',
                        border: 'none',
                        borderRadius: '2.75rem',
                        padding: '0.25rem 0.625rem 0.125rem',
                        width: 'fit-content',
                        font: 'inherit',
                        height: '1.75rem',
                        boxSizing: 'border-box',
                        marginTop: 0,
                        backgroundColor: firstOption?.backgroundColor || '#f5f5f5',
                        color: firstOption?.labelColor || '#000000',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1,
                    }}
                    textstyle={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}
                    text={
                        <>
                            {selectedOptions.length > 0 ? (
                                renderSelectionTooltip(
                                    { style: PILL_TOOLTIP_STYLE },
                                    { style: PILL_TOOLTIP_LIST_STYLE },
                                    <span className={styles.MultiSelectText} style={{ color: firstOption?.labelColor || '#000000' }}>
                                        {firstName}
                                    </span>,
                                    remainingOptions.length > 0 && <span className={styles.More}> +{remainingOptions.length} more</span>,
                                )
                            ) : (
                                <span className={styles.MultiSelectText} style={{ color: firstOption?.labelColor || '#b5b5b5' }}>
                                    {FIELD_PLACEHOLDER.SELECT}
                                </span>
                            )}
                            {!isDisabled && (
                                <div
                                    className={clsx(styles.CaretWrapper, {
                                        [styles.CaretWrapperOpen]: isDropdownOpen,
                                    })}
                                >
                                    <CaretIcon colour={COLORS.GREY} height="13" width="13" />
                                </div>
                            )}
                        </>
                    }
                />
            )}

            {isDropdownOpen && !isDisabled && (
                <div
                    ref={dropdownRef}
                    className={clsx(styles.MultiSelectDropdownCellRenderWrapper, {
                        [styles.isConfigTableModalDropDown]: isConfigTableModalDropDown,
                    })}
                    style={
                        isConfigTableModalDropDown
                            ? {
                                  position: 'fixed',
                                  top: `${dropdownPosition.top}px`,
                                  left: `${dropdownPosition.left}px`,
                                  width: `${dropdownPosition.width}px`,
                                  maxHeight: '320px',
                              }
                            : undefined
                    }
                >
                    <MultiSelectWithDisabledOptions
                        options={allOptions}
                        disabledOptions={[]}
                        selectedItems={selectedValues}
                        placeholder=""
                        toggleSelection={handleToggleSelection}
                        showInput={false}
                        dropdownStyle={
                            isConfigTableModalDropDown
                                ? { width: menuWidth, left: '0px', maxHeight: '225px' }
                                : { width: menuWidth, left: '0px' }
                        }
                        showAddButton={true}
                        addButtonName={column?.actionButtonName || 'Add'}
                        onAddClick={() => {
                            try {
                                if (hasValuesChanged()) {
                                    updateMultiSelectDropdown({
                                        row,
                                        column,
                                        selectedValues: selectedValuesRef.current,
                                    });
                                }
                            } catch (err) {}
                        }}
                    />
                </div>
            )}
        </div>
    );
};
